import { create } from "zustand";

// Estado local do carrinho, só em memória (sem localStorage). O banco é a
// única fonte de verdade — quem busca e persiste é o hook `useCart`
// (src/hooks/useCart.js); este store só guarda o que foi buscado/alterado
// pra renderizar a UI.
const useCartStore = create((set, get) => ({
  cart: [],

  setQuantity: (item) =>
    set((state) => {
      const max = item.qtde ?? Infinity;

      // 🔒 trava o valor entre 0 e max
      const safeQuantity = Math.max(
        0,
        Math.min(max, Number(item.quantity) || 0),
      );

      const findedItem = state.cart.find(
        (cartItem) =>
          cartItem.id === item.id && cartItem.color === item.color,
      );

      // Se não existir
      if (!findedItem) {
        if (safeQuantity <= 0) return { cart: state.cart };

        return {
          cart: [
            ...state.cart,
            {
              ...item,
              sizes: {
                [item.size]: {
                  quantity: safeQuantity,
                  variationId: item.provar_id,
                },
              },
            },
          ],
        };
      }

      // Se for 0 → remove
      if (safeQuantity <= 0) {
        const newSizes = { ...findedItem.sizes };
        delete newSizes[item.size];

        if (Object.keys(newSizes).length === 0) {
          return {
            cart: state.cart.filter(
              (cartItem) =>
                !(cartItem.id === item.id && cartItem.color === item.color),
            ),
          };
        }

        return {
          cart: state.cart.map((cartItem) =>
            cartItem.id === item.id && cartItem.color === item.color
              ? { ...cartItem, sizes: newSizes }
              : cartItem,
          ),
        };
      }

      // Atualiza com valor seguro
      return {
        cart: state.cart.map((cartItem) =>
          cartItem.id === item.id && cartItem.color === item.color
            ? {
                ...cartItem,
                sizes: {
                  ...cartItem.sizes,
                  [item.size]: {
                    quantity: safeQuantity,
                    variationId: item.provar_id,
                  },
                },
              }
            : cartItem,
        ),
      };
    }),

  addToCart: (item) =>
    set((state) => {
      const findedItem = state.cart.find(
        ({ id, color }) => id === item.id && color === item.color,
      );

      if (findedItem) {
        return {
          cart: state.cart.map((cartItem) =>
            cartItem.id === item.id && cartItem.color === item.color
              ? {
                  ...cartItem,
                  sizes: {
                    ...cartItem.sizes,
                    [item.size]: {
                      quantity:
                        (cartItem.sizes[item.size]?.quantity || 0) + 1,
                      variationId: item.provar_id,
                    },
                  },
                }
              : cartItem,
          ),
        };
      }

      return {
        cart: [
          ...state.cart,
          {
            ...item,
            sizes: {
              [item.size]: {
                quantity: 1,
                variationId: item.provar_id,
              },
            },
          },
        ],
      };
    }),

  decrementFromCart: (id, color, size) =>
    set((state) => {
      const findedItem = state.cart.find(
        (cartItem) => cartItem.id === id && cartItem.color === color,
      );

      if (!findedItem) return { cart: state.cart };

      const currentQty = findedItem.sizes[size]?.quantity || 0;

      if (currentQty > 1) {
        return {
          cart: state.cart.map((cartItem) =>
            cartItem.id === id && cartItem.color === color
              ? {
                  ...cartItem,
                  sizes: {
                    ...cartItem.sizes,
                    [size]: {
                      ...cartItem.sizes[size],
                      quantity: currentQty - 1,
                    },
                  },
                }
              : cartItem,
          ),
        };
      }

      const newSizes = { ...findedItem.sizes };
      delete newSizes[size];

      if (Object.keys(newSizes).length === 0) {
        return {
          cart: state.cart.filter(
            (cartItem) => !(cartItem.id === id && cartItem.color === color),
          ),
        };
      }

      return {
        cart: state.cart.map((cartItem) =>
          cartItem.id === id && cartItem.color === color
            ? { ...cartItem, sizes: newSizes }
            : cartItem,
        ),
      };
    }),

  removeFromCart: (id) =>
    set((state) => ({
      cart: state.cart.filter((cartItem) => cartItem.id !== id),
    })),

  clearCart: () => set({ cart: [] }),

  // Substitui o carrinho local pelo carrinho vindo do backend.
  setCart: (cart) => set({ cart: cart ?? [] }),

  totalCartValue: () => {
    const { cart } = get();
    return cart.reduce(
      (total, item) =>
        total +
        Object.values(item.sizes).reduce(
          (sum, s) => sum + s.quantity * item.price,
          0,
        ),
      0,
    );
  },
}));

export default useCartStore;
